import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, MenuItem, Pagination, Select, Typography } from "@mui/material";

import { useProfileData } from "../../../../../hooks/useProfileData";
import { useGetTransactions } from "../../../api/queries/useGetTransactions";
import { useGetInvoices } from "../../../api/queries/useGetInvoices";

import EmptyState from "./EmptyState";
import TableLoading from "./TableLoading";
import TabNavigation from "./TabNavigation";
import InvoicesTable from "./InvoicesTable";
import TransactionsTable from "./TransactionsTable";
import { TransactionStatusEnum } from "../../../enums/payment";

export type TabType = "transactions" | "invoices";

const TransactionsInvoicesTable = () => {
  const { t } = useTranslation("plans", { keyPrefix: "page.plansOverviewPage" });
  const { t: tEnum } = useTranslation("plans", { keyPrefix: "enum.transaction_status" });
  const [activeTab, setActiveTab] = useState<TabType>("transactions");
  const [page, setPage] = useState<number>(0);
  const limit = 10;
  const [status, setStatus] = useState<TransactionStatusEnum | "ALL">("ALL");

  const handleChangeStatus = (status: TransactionStatusEnum | "ALL") => {
    setStatus(status);
    setPage(0);
  };
  const { data: userData } = useProfileData();
  const { id } = userData || {};

  const offset = page * limit;

  const { data: transactions, isLoading: isLoadingTransactions } = useGetTransactions(limit, offset);
  const { data: invoices, isLoading: isLoadingInvoices } = useGetInvoices(id!, limit, offset, status);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(0);
  };

  const isLoading = activeTab === "transactions" ? isLoadingTransactions : isLoadingInvoices;
  const data = activeTab === "transactions" ? transactions : invoices;
  const isEmpty = !isLoading && (!data || !data.items || data.items.length === 0);

  const statusOptions = [
    { value: "ALL", label: t("all") },
    { value: TransactionStatusEnum.Pending, label: tEnum(TransactionStatusEnum.Pending) },
    { value: TransactionStatusEnum.Success, label: tEnum(TransactionStatusEnum.Success) },
    { value: TransactionStatusEnum.Failed, label: tEnum(TransactionStatusEnum.Failed) },
  ];

  return (
    <Box sx={styles.container}>
      <Typography variant="h3" sx={styles.headerTitle}>
        {t("billingAndInvoices")}
      </Typography>

      <Divider flexItem orientation="horizontal" />

      <Box sx={styles.tabNavigationContainer}>
        <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />
        {activeTab === "invoices" && (
          <Box sx={styles.statusFilterContainer}>
            <Typography variant="body1" sx={styles.statusFilterTitle}>
              {t("status")}
            </Typography>
            <Select
              value={status ?? "ALL"}
              onChange={(e) => handleChangeStatus(e.target.value as TransactionStatusEnum | "ALL")}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>
      {isLoading ? (
        <TableLoading type={activeTab} />
      ) : isEmpty ? (
        <EmptyState type={activeTab} />
      ) : (
        <>
          {activeTab === "transactions" ? (
            <TransactionsTable data={transactions?.items!} />
          ) : (
            <InvoicesTable data={invoices?.items!} />
          )}

          {data && data.meta && data.meta.totalPages > 1 && (
            <Pagination
              count={data.meta.totalPages}
              page={page + 1}
              onChange={handleChangePage}
              sx={{
                borderTop: "1px solid var(--Gray-200, #EAECF0)",
                padding: "12px 0 16px",
                direction: "ltr !important",
                "& .MuiPagination-ul": {
                  justifyContent: "center",
                },
                "& .MuiButtonBase-root": {
                  borderRadius: "8px !important",
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default TransactionsInvoicesTable;

const styles = {
  container: {
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--Base-White, #FFF)",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  headerTitle: {
    padding: "20px 16px",
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  tabNavigationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
  },
  statusFilterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusFilterTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
  },
};
